import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { userColumns } from "./datatablesource";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField } from '@mui/material';

const Datatable = () => {
  const [data, setData] = useState([]);
  const [lastDoc, setLastDoc] = useState(null); // State for pagination
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading state for "Load More" button
  const [hasMoreData, setHasMoreData] = useState(true); // Flag to check if there's more data to load
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(""); // State to hold input

  const pageSize = 10; // Number of documents to load per page

  // Fetch initial data with pagination and order by createdAt in descending order
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, orderBy("timestamp", "desc"), limit(pageSize));
      const snapshot = await getDocs(q);

      let list = [];
      snapshot.docs.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      // Store the last document to paginate
      const lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1];

      setData(list);
      setLastDoc(lastVisibleDoc);

      // Check if there are fewer than the pageSize items (no more data to load)
      if (snapshot.docs.length < pageSize) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.log("Error fetching data: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load more data when the "Load More" button is clicked
  const fetchMoreData = async () => {
    if (!lastDoc || !hasMoreData) return;

    setIsLoadingMore(true);
    try {
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        orderBy("timestamp", "desc"),
        startAfter(lastDoc), // Start after the last visible document
        limit(pageSize)
      );

      const snapshot = await getDocs(q);
      let list = [];
      snapshot.docs.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      // Append new data to the existing list
      setData((prevData) => [...prevData, ...list]);

      // Update the last visible document
      const lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(lastVisibleDoc);

      // If fewer than pageSize items are loaded, stop loading more data
      if (snapshot.docs.length < pageSize) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.log("Error fetching more data: ", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Handle dialog open
  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  // Delete a user
  const handleDelete = async () => {
    if (deleteConfirmation === "DELETE") {
      try {
        await deleteDoc(doc(db, "users", deleteId));
        setData(data.filter((item) => item.id !== deleteId));
        setDeleteDialogOpen(false);
        setDeleteConfirmation(""); // Reset the confirmation input
      } catch (err) {
        console.log(err);
      }
    }
  };

  // Define action column
  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <Link
              to={`/users/fullview?uid=${params.row.id}`}
              style={{ textDecoration: "none" }}
            >
              <div className="viewButton">View</div>
            </Link>
            <div
              className="deleteButton"
              onClick={() => openDeleteDialog(params.row.id)} // Open dialog on delete click
            >
              Delete
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="datatable">
      <DataGrid
        className="datagrid"
        rows={data}
        columns={userColumns.concat(actionColumn)}
        pageSize={pageSize}
        rowsPerPageOptions={[10]}
        checkboxSelection
        loading={isLoading}
      />

      {/* Load More Button */}
      {hasMoreData && (
        <div className="loadMoreContainer">
          <button
            className="loadMoreButton"
            onClick={fetchMoreData}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <p>Type "DELETE" to confirm deletion:</p>
          <TextField
            autoFocus
            margin="dense"
            label="Confirmation"
            type="text"
            fullWidth
            variant="outlined"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Datatable;
